import React, { useState, useEffect, useRef } from 'react';
import { Store, Upload, MapPin } from 'lucide-react';
import ApiServices from '../services/ApiServices';
import { ShowDangerToast, ShowSuccessToast } from '../services/toast';

import axios from 'axios';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

mapboxgl.accessToken = 'pk.eyJ1IjoiZG9ub3JkaXNwYXRjaCIsImEiOiJjbWMwdjZrZWIwNnU2MmxwdHBsaGkzcm9rIn0.u70VsaQCG6q1r8SLZ8nVLg';

const AddStore = () => {
  const [formData, setFormData] = useState({
    name: '',
    ownerEmail: '',
    ownerPassword: '',
    username: '',
    ownerProfilePicture: null,
    storePictures: [],
  });

  const [geofence, setGeofence] = useState({
    address: '',
    latitude: '',
    longitude: '',
  });
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isAutocompleteLoading, setIsAutocompleteLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const logoPreview = useRef(null);

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const autocompleteTimeoutRef = useRef(null);

  useEffect(() => {
    if (geofence.latitude && geofence.longitude && !mapRef.current) {
      const lat = parseFloat(geofence.latitude);
      const lng = parseFloat(geofence.longitude);
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [lng, lat],
        zoom: 13,
      });
      mapRef.current.addControl(new mapboxgl.NavigationControl());
      markerRef.current = new mapboxgl.Marker().setLngLat([lng, lat]).addTo(mapRef.current);
    }
  }, [geofence.latitude, geofence.longitude]);

  useEffect(() => {
    if (mapRef.current && markerRef.current && geofence.latitude && geofence.longitude) {
      const lat = parseFloat(geofence.latitude);
      const lng = parseFloat(geofence.longitude);
      mapRef.current.flyTo({ center: [lng, lat], zoom: 13 });
      markerRef.current.setLngLat([lng, lat]);
    }
  }, [geofence.latitude, geofence.longitude]);

  const handleAddressChange = async (e) => {
    const query = e.target.value;
    setGeofence({ ...geofence, address: query });
    if (autocompleteTimeoutRef.current) clearTimeout(autocompleteTimeoutRef.current);
    if (query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    autocompleteTimeoutRef.current = setTimeout(async () => {
      setIsAutocompleteLoading(true);
      try {
        const { data } = await axios.get(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`,
          { params: { access_token: mapboxgl.accessToken, limit: 5 } }
        );
        setAddressSuggestions(
          data.features.map(f => ({
            formattedAddress: f.place_name,
            latitude: f.geometry.coordinates[1],
            longitude: f.geometry.coordinates[0],
          }))
        );
      } catch {
        setAddressSuggestions([]);
      } finally {
        setIsAutocompleteLoading(false);
      }
    }, 300);
  };

  const handleInputChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, ownerProfilePicture: file });
      if (logoPreview.current) URL.revokeObjectURL(logoPreview.current);
      logoPreview.current = URL.createObjectURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!geofence.address || !geofence.latitude || !geofence.longitude) {
      ShowDangerToast('Please select an address.');
      return;
    }
    setIsSubmitting(true);
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([k, v]) => {
        if (k === 'storePictures') v.forEach(file => data.append(k, file));
        else if (v) data.append(k, v);
      });
      data.append('address', geofence.address);
      data.append('latitude', geofence.latitude);
      data.append('longitude', geofence.longitude);
      await ApiServices.postFormData('/stores', data);
      ShowSuccessToast('Store created successfully');
    } catch {
      ShowDangerToast('Failed to create store');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fields = [
    { label: 'Store Name', name: 'name', type: 'text' },
    { label: 'Owner Email', name: 'ownerEmail', type: 'email' },
    { label: 'Owner Password', name: 'ownerPassword', type: 'password' },
    { label: 'Username', name: 'username', type: 'text' },
  ];

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-navy-800">Add New Store</h2>
        <p className="text-sm text-gray-500 mt-0.5">Create a new store and assign an owner.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-5">
            <Store className="w-5 h-5 text-navy-600" />
            <h3 className="text-base font-semibold text-navy-800">Store Details</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fields.map((item, i) => (
              <div key={i}>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  {item.label}
                </label>
                <input
                  type={item.type}
                  name={item.name}
                  value={formData[item.name]}
                  onChange={handleInputChange}
                  className="input-field"
                  autoComplete={item.type === 'password' ? 'new-password' : 'off'}
                  required
                />
              </div>
            ))}
          </div>

          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Owner Profile Picture</label>
            <div className="flex items-center gap-4">
              {logoPreview.current ? (
                <img
                  src={logoPreview.current}
                  alt="Preview"
                  className="h-14 w-14 object-cover rounded-xl border border-gray-200 bg-gray-50"
                />
              ) : (
                <div className="h-14 w-14 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
                  <Upload className="w-5 h-5 text-gray-400" />
                </div>
              )}
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => document.getElementById('owner-photo-input').click()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-navy-50 text-navy-700 hover:bg-navy-100 border border-navy-200 transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  {logoPreview.current ? 'Change Photo' : 'Upload Photo'}
                </button>
                <input id="owner-photo-input" type="file" className="hidden" onChange={handleFileChange} accept="image/*" />
                <p className="text-xs text-gray-400">PNG, JPG up to 2MB</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-5">
            <MapPin className="w-5 h-5 text-navy-600" />
            <h3 className="text-base font-semibold text-navy-800">Location</h3>
          </div>

          <div className="relative">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              Address
            </label>
            <input
              type="text"
              value={geofence.address}
              onChange={handleAddressChange}
              className="input-field"
              required
              autoComplete="off"
              placeholder="Start typing an address..."
            />
            {addressSuggestions.length > 0 && (
              <ul className="absolute z-50 border border-gray-200 rounded-xl mt-1 w-full max-h-60 overflow-auto bg-white shadow-lg">
                {isAutocompleteLoading && (
                  <li className="px-4 py-2.5 text-sm italic text-gray-400">
                    Loading suggestions...
                  </li>
                )}
                {addressSuggestions.map((address, index) => (
                  <li
                    key={index}
                    onClick={() => {
                      setGeofence({
                        ...geofence,
                        address: address.formattedAddress,
                        latitude: address.latitude,
                        longitude: address.longitude,
                      });
                      setAddressSuggestions([]);
                    }}
                    className="px-4 py-2.5 cursor-pointer text-sm text-gray-700 hover:bg-navy-50 hover:text-navy-700 transition-colors first:rounded-t-xl last:rounded-b-xl"
                  >
                    {address.formattedAddress}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {geofence.latitude && geofence.longitude && (
            <div className="mt-4">
              <div
                ref={mapContainerRef}
                style={{ width: '100%', height: '280px' }}
                className="rounded-xl overflow-hidden border border-gray-200"
              />
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full sm:w-auto px-6 py-2.5 bg-navy-600 text-white rounded-lg hover:bg-navy-700 text-sm font-medium transition-colors disabled:opacity-50"
        >
          {isSubmitting ? 'Creating...' : 'Create Store'}
        </button>
      </form>
    </div>
  );
};

export default AddStore;
